import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
} from 'firebase/firestore';
import {FirestoreCollection} from 'hubbl-shared';
import {firestore} from '../firebase';
import {EventAnalytics} from 'hubbl-shared';

const getEventAnalytics = async (
  companyId: string,
): Promise<EventAnalytics.EventAnalytics | null> => {
  try {
    // First try to get current live analytics
    const currentDocRef = doc(
      firestore,
      FirestoreCollection.Companies,
      companyId,
      FirestoreCollection.EventAnalytics,
      'current',
    );
    const currentDocSnap = await getDoc(currentDocRef);

    if (currentDocSnap.exists()) {
      return currentDocSnap.data() as EventAnalytics.EventAnalytics;
    }

    // Fallback: Get the current month's snapshot
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const snapshotId = `${currentYear}-${currentMonth.toString().padStart(2, '0')}`;

    const snapshotDocRef = doc(
      firestore,
      FirestoreCollection.Companies,
      companyId,
      FirestoreCollection.EventAnalytics,
      snapshotId,
    );
    const snapshotDocSnap = await getDoc(snapshotDocRef);

    if (snapshotDocSnap.exists()) {
      return snapshotDocSnap.data() as EventAnalytics.EventAnalytics;
    }

    return null;
  } catch (error) {
    console.error('Error fetching event analytics:', error);
    return null;
  }
};

const getEventAnalyticsHistory = async (
  companyId: string,
  months: number = 12,
): Promise<EventAnalytics.EventAnalytics[]> => {
  try {
    const collectionRef = collection(
      firestore,
      FirestoreCollection.Companies,
      companyId,
      FirestoreCollection.EventAnalytics,
    );

    const q = query(
      collectionRef,
      where('snapshotDate', '!=', null),
      orderBy('snapshotDate', 'desc'),
      limit(months),
    );

    const querySnapshot = await getDocs(q);
    const analytics: EventAnalytics.EventAnalytics[] = [];

    querySnapshot.forEach(doc => {
      analytics.push(doc.data() as EventAnalytics.EventAnalytics);
    });

    return analytics;
  } catch (error) {
    console.error('Error fetching event analytics history:', error);
    return [];
  }
};

// Get ALL monthly snapshots for charts (including current month if it exists)
const getAllEventAnalyticsSnapshots = async (
  companyId: string,
): Promise<EventAnalytics.EventAnalytics[]> => {
  try {
    const collectionRef = collection(
      firestore,
      FirestoreCollection.Companies,
      companyId,
      FirestoreCollection.EventAnalytics,
    );

    // Get all snapshots (both finalized monthly ones and current month)
    const q = query(
      collectionRef,
      orderBy('year', 'asc'),
      orderBy('month', 'asc'),
    );

    const querySnapshot = await getDocs(q);
    const analytics: EventAnalytics.EventAnalytics[] = [];

    querySnapshot.forEach(doc => {
      analytics.push(doc.data() as EventAnalytics.EventAnalytics);
    });

    return analytics;
  } catch (error) {
    console.error('Error fetching all event analytics snapshots:', error);
    return [];
  }
};

const getEventAnalyticsSummary = async (
  companyId: string,
): Promise<
  | (EventAnalytics.EventAnalyticsSummary & {
      timeframeLabel?: string;
      startDate?: string;
      endDate?: string;
    })
  | null
> => {
  try {
    // Get current month analytics and last 3 months for quarterly view
    const [currentAnalytics, historyAnalytics] = await Promise.all([
      getEventAnalytics(companyId),
      getEventAnalyticsHistory(companyId, 3), // Last 3 months for quarterly data
    ]);

    if (!currentAnalytics) return null;

    // Combine current and historical data for quarterly view
    const allAnalytics = [currentAnalytics, ...historyAnalytics];

    // Aggregate quarterly data
    const quarterlyData = aggregateQuarterlyData(allAnalytics);

    const summary: EventAnalytics.EventAnalyticsSummary & {
      timeframeLabel?: string;
      startDate?: string;
      endDate?: string;
    } = {
      totalEvents: quarterlyData.totalEvents,
      averageResponseRate: quarterlyData.averageResponseRate,
      topPerformingEvent: quarterlyData.topPerformingEvent,
      evaluationCompletionRate: quarterlyData.averageEvaluationCompletionRate,
      upcomingEvents: currentAnalytics.eventDistribution.upcomingEvents, // Keep current for upcoming
      recentEvents: quarterlyData.recentEvents,
      timeframeLabel: quarterlyData.timeframeLabel,
      startDate: quarterlyData.startDate,
      endDate: quarterlyData.endDate,
    };

    return summary;
  } catch (error) {
    console.error('Error fetching event analytics summary:', error);
    return null;
  }
};

// Helper functions
const aggregateQuarterlyData = (
  allAnalytics: EventAnalytics.EventAnalytics[],
) => {
  if (allAnalytics.length === 0) {
    return {
      totalEvents: 0,
      averageResponseRate: 0,
      averageEvaluationCompletionRate: 0,
      topPerformingEvent: null,
      recentEvents: [],
      timeframeLabel: 'Last 3 Months',
      startDate: new Date().toISOString(),
      endDate: new Date().toISOString(),
    };
  }

  // Aggregate all event performance data
  const allEventPerformance: Record<
    string,
    EventAnalytics.EventPerformanceData
  > = {};
  let totalEvents = 0;
  let totalInvited = 0;
  let totalResponses = 0;

  // Find date range
  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1); // 3 months ago
  const endDate = now;

  allAnalytics.forEach(analytics => {
    totalEvents += analytics.totalEvents;
    totalInvited += analytics.totalInvitedUsers;
    totalResponses += analytics.responseMetrics.totalResponses;

    // Combine event performance data
    Object.assign(allEventPerformance, analytics.eventPerformance);
  });

  const averageResponseRate =
    totalInvited > 0 ? (totalResponses / totalInvited) * 100 : 0;
  // Fix: Calculate evaluation completion rate by averaging individual event completion rates
  let totalCompletionRate = 0;
  let eventsWithEvaluationsCount = 0;

  allAnalytics.forEach(analytics => {
    Object.values(analytics.eventPerformance).forEach(event => {
      if (event.hasEvaluation) {
        totalCompletionRate += event.evaluationCompletionRate;
        eventsWithEvaluationsCount++;
      }
    });
  });

  const averageEvaluationCompletionRate =
    eventsWithEvaluationsCount > 0
      ? totalCompletionRate / eventsWithEvaluationsCount
      : 0;

  return {
    totalEvents,
    averageResponseRate: Math.round(averageResponseRate),
    averageEvaluationCompletionRate: Math.round(
      averageEvaluationCompletionRate,
    ),
    topPerformingEvent: getTopPerformingEvent(allEventPerformance),
    recentEvents: getRecentEvents(allEventPerformance),
    timeframeLabel: 'Last 3 Months',
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
};

const getTopPerformingEvent = (
  eventPerformance: Record<string, EventAnalytics.EventPerformanceData>,
): EventAnalytics.EventPerformanceData | null => {
  const events = Object.values(eventPerformance);
  if (events.length === 0) return null;

  return events.reduce((top, current) =>
    current.responseRate > top.responseRate ? current : top,
  );
};

const getRecentEvents = (
  eventPerformance: Record<string, EventAnalytics.EventPerformanceData>,
): EventAnalytics.EventPerformanceData[] => {
  return Object.values(eventPerformance)
    .sort(
      (a, b) =>
        new Date(b.startDateTime).getTime() -
        new Date(a.startDateTime).getTime(),
    )
    .slice(0, 5);
};

const migrateEventAnalytics = async (
  companyId: string,
): Promise<{success: boolean; message: string}> => {
  try {
    // For migration, we still need to call the Cloud Function
    // since it's a one-time operation that processes all events
    const {httpsCallable} = await import('firebase/functions');
    const {firebaseFunctions} = await import('../firebase');

    const migrateFunction = httpsCallable(
      firebaseFunctions,
      'onMigrateEventAnalytics',
    );
    const result = await migrateFunction({companyId});

    return {
      success: true,
      message:
        (result.data as {message?: string})?.message ||
        'Event analytics migration completed',
    };
  } catch (error) {
    console.error('Error migrating event analytics:', error);
    return {
      success: false,
      message: 'Failed to migrate event analytics',
    };
  }
};

export const ApiEventAnalytics = {
  getEventAnalytics,
  getEventAnalyticsHistory,
  getAllEventAnalyticsSnapshots,
  getEventAnalyticsSummary,
  migrateEventAnalytics,
};
